import React, { useState } from 'react';
import { useParams, Link,useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {  FiBook, FiAward, FiTrash2, FiPlus, FiEye, FiX } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import Avatar from '../components/Avatar';


const ClassroomDetail = () => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showAddQuizModal, setShowAddQuizModal] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { data: classroom, isLoading, refetch } = useQuery({
    queryKey: ['classroom', id],
    queryFn: () => axios.get(`/api/classrooms/${id}`).then(res => res.data.classroom)
  });

  const { data: availableQuizzes } = useQuery({
    queryKey: ['availableQuizzes'],
    queryFn: () => axios.get('/api/quizzes').then(res => res.data.quizzes),
    enabled: isAdmin && showAddQuizModal
  });

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student?')) return;

    try {
      await axios.delete(`/api/classrooms/${id}/students/${studentId}`);
      toast.success('Student removed successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to remove student');
    }
  };

  const handleAddQuiz = async () => {
    if (!selectedQuizId) {
      toast.error('Please select a quiz');
      return;
    }

    try {
      await axios.post('/api/classrooms/add-quiz', {
        classroomId: id,
        quizId: selectedQuizId
      });
      toast.success('Quiz added successfully');
      setShowAddQuizModal(false);
      setSelectedQuizId('');
      refetch();
    } catch (error) {
      toast.error('Failed to add quiz');
    }
  };

  const handleRemoveQuiz = async (quizId, quizTitle) => {
    if (!window.confirm(`Are you sure you want to remove "${quizTitle}" from this classroom?`)) {
      return;
    }

    try {
      await axios.delete(`/api/classrooms/${id}/quizzes/${quizId}`);
      toast.success('Quiz removed from classroom successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to remove quiz');
    }
  };

  const handleDeleteClassroom = async () => {
    if (!window.confirm('Are you sure you want to delete this classroom? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`/api/classrooms/${id}`);
      toast.success('Classroom deleted successfully');
      navigate('/classrooms');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete classroom');
      setDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!classroom) {
    return <div>Classroom not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{classroom.name}</h1>
          <p className="text-gray-600 mt-2">
            Classroom Code: <span className="font-mono font-bold text-lg">{classroom.code}</span>
          </p>
          <p className="text-sm text-gray-500">
            Created by: {classroom.admin.name}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/leaderboard/classroom/${id}`}
            className="btn-primary"
          >
            <FiAward className="inline mr-2" />
            View Leaderboard
          </Link>
          {isAdmin && (
            <button
              onClick={handleDeleteClassroom}
              disabled={deleting}
              className="btn-secondary text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
            >
              <FiTrash2 className="inline mr-2" />
              Delete Classroom
            </button>
          )}
        </div>
      </div>

      {/* Quizzes Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Assigned Quizzes</h2>
          {isAdmin && (
            <button
              onClick={() => setShowAddQuizModal(true)}
              className="btn-secondary text-sm"
            >
              <FiPlus className="inline mr-1" />
              Add Quiz
            </button>
          )}
        </div>

        {classroom.quizzes.length === 0 ? (
          <p className="text-gray-500">No quizzes assigned yet</p>
        ) : (
          <div className="space-y-3">
            {classroom.quizzes.map((quiz) => (
              <div
                key={quiz._id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <FiBook className="text-gray-400" />
                  <div>
                    <h3 className="font-medium text-gray-900">{quiz.title}</h3>
                    <p className="text-sm text-gray-500">
                      {quiz.questions?.length || 0} questions • 
                      {quiz.isActive ? (
                        <span className="text-green-600 ml-2">Active</span>
                      ) : (
                        <span className="text-red-600 ml-2">Inactive</span>
                      )}
                      {quiz.hasAttempted && (
                        <span className="text-blue-600 ml-2">• Attempted</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Student Actions */}
                  {quiz.isActive && !isAdmin && !quiz.hasAttempted && (
                    <Link
                      to={`/quizzes/${quiz._id}/attempt?classroom=${id}`}
                      state={{ classroomId: id }}
                      className="btn-primary text-sm"
                    >
                      Attempt Quiz
                    </Link>
                  )}
                  {quiz.isActive && !isAdmin && quiz.hasAttempted && (
                    <>
                      <button
                        disabled
                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-200 rounded-lg cursor-not-allowed"
                      >
                        ✓ Attempted
                      </button>
                      <Link
                        to={`/quizzes/${quiz._id}/view-result`}
                        className="btn-secondary text-sm"
                      >
                        <FiEye className="inline mr-1" />
                        View Answers
                      </Link>
                    </>
                  )}
                  
                  {/* Admin Actions */}
                  {isAdmin && (
                    <>
                      <Link
                        to={`/quizzes/${quiz._id}`}
                        className="text-primary-600 hover:text-primary-700"
                        title="View Quiz Details"
                      >
                        <FiEye />
                      </Link>
                      <button
                        onClick={() => handleRemoveQuiz(quiz._id, quiz.title)}
                        className="text-red-600 hover:text-red-700"
                        title="Remove from classroom"
                      >
                        <FiX size={20} />
                      </button>
                    </>
                  )}
                  
                  {/* Common Actions */}
                  <Link
                    to={`/leaderboard/quiz/${quiz._id}`}
                    className="text-primary-600 hover:text-primary-700"
                    title="View Leaderboard"
                  >
                    <FiAward />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Students Section */}
      {isAdmin && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Students ({classroom.students.length})
          </h2>
          {classroom.students.length === 0 ? (
            <p className="text-gray-500">No students enrolled yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {classroom.students.map((student) => (
                    <tr key={student._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Avatar name={student.name} size="small" className="mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {student.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.mobileNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleRemoveStudent(student._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Quiz Modal */}
      {showAddQuizModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Add Quiz to Classroom
            </h2>
            <select
              value={selectedQuizId}
              onChange={(e) => setSelectedQuizId(e.target.value)}
              className="input-field mb-4"
            >
              <option value="">Select a quiz</option>
              {availableQuizzes?.map((quiz) => (
                <option key={quiz._id} value={quiz._id}>
                  {quiz.title} {quiz.isActive ? '(Active)' : '(Inactive)'}
                </option>
              ))}
            </select>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddQuizModal(false);
                  setSelectedQuizId('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQuiz}
                className="btn-primary"
              >
                Add Quiz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomDetail;